// frontend/src/components/mcbim/BuildingStoryMenu.tsx
import React, { useMemo } from "react";
import { Badge, Box, Button, Loader, Menu, Text, TextInput, Tooltip } from "@mantine/core";
import { MdLayers } from "react-icons/md";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useGetMcbimBuildingStoriesQuery } from "../../../openapi";
import { selectBuildingStoryList } from "../designSelector";
import { setBuildingStoryListRedux, setFloorAndJobInRedux } from "../designSlice";

export const BuildingStoryMenu: React.FC = () => {
  const dispatch = useAppDispatch();

  const projectNo = useAppSelector((state) => state.design.projectNo);
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);
  const buildingStoriesState = useAppSelector((state) => state.design.buildingStoryList);

  const {
    data: buildingStories = [],
    isLoading,
    isError,
  } = useGetMcbimBuildingStoriesQuery({ projectNo: projectNo! }, { skip: !projectNo });

  const filteredStories = useAppSelector(selectBuildingStoryList);

  const makeValue = (name: string, jobId: string) => `${name}__${jobId}`;

  const currentValue = useMemo(() => {
    if (!floorAndJob) return null;
    return makeValue(floorAndJob.floorName, floorAndJob.jobId);
  }, [floorAndJob]);

  const currentLabel = useMemo(() => {
    if (!floorAndJob) return "Velg etasje";
    return floorAndJob.floorName;
  }, [floorAndJob]);

  const tooltipLabel = floorAndJob ? "Endre etasje" : "Velg etasje";

  const setSearch = (search: string) => {
    dispatch(
      setBuildingStoryListRedux({
        ...buildingStoriesState,
        search,
      })
    );
  };

  const handleSelect = (value: string) => {
    const story = filteredStories.find((s) => makeValue(s.name, String(s.job.jobId)) === value);
    if (!story) return;

    const storyJobId = String(story.job.jobId);
    dispatch(
      setFloorAndJobInRedux({
        floorName: story.name,
        jobId: storyJobId,
        floorGlobalId: story.globalId,
      })
    );
  };

  // Loading / error / no data: vis knapp i "disabled" stil
  if (isLoading) {
    return (
      <Button
        size="sm"
        variant="light"
        leftSection={<Loader size="xs" />}
        disabled
        style={{ width: 220, justifyContent: "flex-start" }}
      >
        Laster etasjer…
      </Button>
    );
  }

  if (isError || !buildingStories || buildingStories.length === 0) {
    return (
      <Button
        size="sm"
        variant="light"
        leftSection={<MdLayers size={16} />}
        disabled
        style={{ width: 220, justifyContent: "flex-start" }}
      >
        {isError ? "Kunne ikke laste etasjer" : "Ingen etasjer funnet"}
      </Button>
    );
  }

  return (
    <Menu withinPortal position="bottom-start" shadow="md">
      <Menu.Target>
        <Tooltip label={tooltipLabel} withArrow position="bottom">
          <Button
            size="sm"
            variant="subtle"
            leftSection={<MdLayers size={16} />}
            rightSection={<FiChevronDown size={14} />}
            style={{ width: 220, justifyContent: "flex-start" }}
          >
            <Text truncate>{currentLabel}</Text>
          </Button>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        {/* Søkefelt i toppen av dropdown */}
        <Box p="xs">
          <TextInput
            value={buildingStoriesState.search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Søk på etasjenavn…"
            size="xs"
          />
        </Box>

        {/* Scrollbar for etasjeliste */}
        <Box
          style={{
            maxHeight: 260,
            overflowY: "auto",
            paddingInline: 4,
          }}
        >
          {filteredStories.length === 0 ? (
            <Box px="xs" pb="xs">
              <Text size="xs" c="dimmed">
                Ingen etasjer matcher søket.
              </Text>
            </Box>
          ) : (
            filteredStories.map((story) => {
              const value = makeValue(story.name, String(story.job.jobId));
              const isSelected = value === currentValue;

              return (
                <Menu.Item
                  key={story.index}
                  onClick={() => handleSelect(value)}
                  leftSection={isSelected ? <FiCheck size={14} /> : <span style={{ width: 14 }} />}
                  rightSection={
                    <Badge variant="light" size="xs" px={6}>
                      {story.spaceCount} ROM
                    </Badge>
                  }
                >
                  <Text size="sm" truncate>
                    {story.name}
                  </Text>
                </Menu.Item>
              );
            })
          )}
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};
